import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Save, AlertTriangle } from "lucide-react";

interface Attribute {
  thinking_method_id: string;
  attribute_name: string;
  attribute_value: string;
  created_at: string;
  updated_at: string;
}

interface ThinkingMethodInfo {
  id: string;
  name: string;
  icon_url: string | null;
  author: string;
  status: string;
}

interface LensPropertiesProps {
  initialMethodId?: string | null;
}

const isJsonAttribute = (name: string) => name.toUpperCase().includes("JSON");

const validateJson = (value: string): string | null => {
  try {
    JSON.parse(value);
    return null;
  } catch (error: unknown) {
    return error instanceof Error ? error.message : "Invalid JSON";
  }
};

export const LensProperties = ({ initialMethodId }: LensPropertiesProps) => {
  const [methods, setMethods] = useState<ThinkingMethodInfo[]>([]);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(initialMethodId ?? null);
  const selectedMethodRef = useRef<string | null>(initialMethodId ?? null);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [loadingAttrs, setLoadingAttrs] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [savingAttr, setSavingAttr] = useState<string | null>(null);
  const [jsonErrors, setJsonErrors] = useState<Record<string, string | null>>({});
  const attributeRequest = useRef(0);
  const { toast } = useToast();

  const fetchMethods = useCallback(async () => {
    const { data, error } = await supabase
      .from("thinking_methods")
      .select("id, name, icon_url, author, status")
      .order("order_index");
    if (error) {
      toast({ title: "Error", description: "Failed to fetch lenses", variant: "destructive" });
    } else {
      setMethods(data || []);
    }
    setLoadingMethods(false);
  }, [toast]);

  const fetchAttributes = useCallback(async (methodId: string) => {
    const request = ++attributeRequest.current;
    setLoadingAttrs(true);
    const { data, error } = await supabase
      .from("thinking_method_attributes")
      .select("*")
      .eq("thinking_method_id", methodId)
      .order("attribute_name");
    if (request !== attributeRequest.current) return;
    if (error) {
      toast({ title: "Error", description: "Failed to fetch attributes", variant: "destructive" });
      setAttributes([]);
    } else {
      setAttributes(data || []);
    }
    setLoadingAttrs(false);
  }, [toast]);

  useEffect(() => {
    void fetchMethods();
  }, [fetchMethods]);

  useEffect(() => {
    selectedMethodRef.current = selectedMethodId;
    setAttributes([]);
    setEditedValues({});
    setJsonErrors({});
    if (selectedMethodId) {
      void fetchAttributes(selectedMethodId);
    }
    return () => { attributeRequest.current += 1; };
  }, [selectedMethodId, fetchAttributes]);

  const handleValueChange = useCallback((attrName: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [attrName]: value }));
    if (isJsonAttribute(attrName)) {
      setJsonErrors((prev) => ({ ...prev, [attrName]: validateJson(value) }));
    }
  }, []);

  const handleSave = async (attr: Attribute) => {
    const newValue = editedValues[attr.attribute_name];
    if (newValue === undefined) return;

    if (isJsonAttribute(attr.attribute_name)) {
      const err = validateJson(newValue);
      if (err) {
        toast({ title: "Invalid JSON", description: err, variant: "destructive" });
        return;
      }
    }

    setSavingAttr(attr.attribute_name);
    const { error } = await supabase
      .from("thinking_method_attributes")
      .update({ attribute_value: newValue, updated_at: new Date().toISOString() })
      .eq("thinking_method_id", attr.thinking_method_id)
      .eq("attribute_name", attr.attribute_name);

    if (error) {
      toast({ title: "Error", description: "Failed to save attribute", variant: "destructive" });
    } else {
      toast({ title: "Saved", description: `${attr.attribute_name} updated successfully` });
      if (selectedMethodRef.current === attr.thinking_method_id) {
        setEditedValues((prev) => {
          const next = { ...prev };
          delete next[attr.attribute_name];
          return next;
        });
        // Read the saved value and timestamp from the server, including any normalization.
        await fetchAttributes(attr.thinking_method_id);
      }
    }
    setSavingAttr(null);
  };

  const getDisplayValue = (attr: Attribute) =>
    editedValues[attr.attribute_name] ?? attr.attribute_value;

  const isDirty = (attrName: string) => editedValues[attrName] !== undefined;

  if (loadingMethods) {
    return <div className="text-center p-8 text-muted-foreground">Loading lenses...</div>;
  }

  const selectedMethod = methods.find((m) => m.id === selectedMethodId);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Lens Properties</h2>
        <p className="text-muted-foreground">Select a lens to view and edit its attributes</p>
      </div>

      <div className="grid gap-2">
        {methods.map((method) => (
          <button
            key={method.id}
            onClick={() => { selectedMethodRef.current = method.id; setSelectedMethodId(method.id); }}
            className={cn(
              "flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors",
              selectedMethodId === method.id
                ? "border-primary bg-primary/5"
                : "border-border/50 hover:bg-muted/50"
            )}
          >
            {method.icon_url && (
              <img src={method.icon_url} alt={method.name} className="w-8 h-8 rounded-md object-cover" />
            )}
            <div className="flex-1 min-w-0">
              <span className="font-medium text-sm text-foreground">{method.name}</span>
              <span className="block text-xs text-muted-foreground">{method.author}</span>
            </div>
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] px-1.5 py-0 capitalize",
                method.status === "published" && "border-emerald-500/50 bg-emerald-500/10 text-emerald-400",
                method.status === "draft" && "border-yellow-500/50 bg-yellow-500/10 text-yellow-400",
              )}
            >
              {method.status}
            </Badge>
          </button>
        ))}
      </div>

      {selectedMethodId && selectedMethod && (
        <Card>
          <CardHeader>
            <CardTitle>{selectedMethod.name}</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingAttrs ? (
              <div className="text-center p-4 text-muted-foreground">Loading attributes...</div>
            ) : attributes.length === 0 ? (
              <div className="text-center p-4 text-muted-foreground">No attributes found.</div>
            ) : (
              <Tabs defaultValue={attributes[0]?.attribute_name}>
                <TabsList className="flex flex-wrap h-auto gap-1">
                  {attributes.map((attr) => (
                    <TabsTrigger key={attr.attribute_name} value={attr.attribute_name} className="text-xs">
                      {attr.attribute_name}
                      {isDirty(attr.attribute_name) && (
                        <span className="ml-1 w-1.5 h-1.5 rounded-full bg-primary inline-block" />
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {attributes.map((attr) => {
                  const currentValue = getDisplayValue(attr);
                  const dirty = isDirty(attr.attribute_name);
                  const jsonErr = isJsonAttribute(attr.attribute_name) ? jsonErrors[attr.attribute_name] : null;

                  return (
                    <TabsContent key={attr.attribute_name} value={attr.attribute_name}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{currentValue.length.toLocaleString()} chars</Badge>
                          <span className="text-xs text-muted-foreground">
                            Updated: {new Date(attr.updated_at).toLocaleString()}
                          </span>
                          {isJsonAttribute(attr.attribute_name) && (
                            <Badge variant="outline" className="text-[10px]">JSON</Badge>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleSave(attr)}
                          disabled={!dirty || savingAttr === attr.attribute_name || !!jsonErr}
                          className="gap-1.5"
                        >
                          <Save className="w-3.5 h-3.5" />
                          {savingAttr === attr.attribute_name ? "Saving..." : "Save"}
                        </Button>
                      </div>

                      {jsonErr && dirty && (
                        <div className="flex items-center gap-2 mb-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                          <span>Invalid JSON: {jsonErr}</span>
                        </div>
                      )}

                      <textarea
                        className={cn(
                          "w-full h-[500px] rounded-md border p-4 text-sm font-mono bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-primary/30",
                          jsonErr && dirty && "border-destructive focus:ring-destructive/30"
                        )}
                        value={currentValue}
                        onChange={(e) => handleValueChange(attr.attribute_name, e.target.value)}
                        spellCheck={false}
                      />
                    </TabsContent>
                  );
                })}
              </Tabs>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
